import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api, ApiError, type FeedbackListState } from "@/lib/api";
import { cn } from "@/lib/utils";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { FeedbackReportsTable } from "./FeedbackReportsTable";

// The Feedback tab on the admin container detail page — the one place in the
// dashboard that shows runtime feedback reports: this Boo's, newest first,
// with download, archive/reopen, and a detail sheet. Reads the brain's own
// list through boo-backend (summary rows), one page at a time; nothing here
// spans the fleet.

const FILTERS: Array<{ value: FeedbackListState; label: string }> = [
  { value: "open", label: "Open" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

const EMPTY_TEXT: Record<FeedbackListState, string> = {
  open: "No open reports. A Boo files one when its runtime feedback is switched on and something in its own runtime misbehaves.",
  archived: "No archived reports yet. Archive a report from its detail view once it has been looked at.",
  all: "No reports yet. A Boo files one when its runtime feedback is switched on and something in its own runtime misbehaves.",
};

export function ContainerFeedbackPanel({ containerId }: { containerId: string }) {
  const [state, setState] = useState<FeedbackListState>("open");
  const query = useInfiniteQuery({
    queryKey: ["admin-container-feedback", containerId, state],
    queryFn: ({ pageParam }) =>
      api.admin.containerFeedbackSummary(containerId, { state, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    // Keep the current rows on screen while a filter switch loads.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Neither an outdated Boo (409), a missing one (404), nor a denied
    // session (401/403) changes on retry.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && [401, 403, 404, 409].includes(error.status)) &&
      failureCount < 2,
  });

  if (query.isLoading) return <LoadingIndicator message="Asking this Boo for its reports…" />;
  if (query.isError) {
    const err = query.error;
    if (err instanceof ApiError && err.code === "brain_outdated") {
      return (
        <p className="text-sm text-muted-foreground">
          This Boo runs a version without runtime feedback. Deploy a newer image from the Image
          tab to enable it.
        </p>
      );
    }
    return <ErrorComponent error={err} />;
  }

  const pages = query.data?.pages ?? [];
  const reports = pages.flatMap((page) => page.reports);
  // A Boo that predates archiving answers no counts; the tabs then show none.
  const counts = pages[0]?.counts ?? null;
  const countFor = (value: FeedbackListState): number | null =>
    counts ? (value === "all" ? counts.open + counts.archived : counts[value]) : null;
  const total = countFor(state);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">
          Diagnostic packages this Boo filed about its own tools, skills, scheduled tasks, working
          space, or reasoning. Each package holds that turn&apos;s conversation.
        </p>
        <div className="ml-auto flex items-center gap-3">
          <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Which reports to show">
            {FILTERS.map((f) => {
              const n = countFor(f.value);
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setState(f.value)}
                  className={cn(
                    "rounded px-3 py-1 text-sm transition-colors",
                    state === f.value
                      ? "bg-muted font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {f.label}
                  {n != null ? ` (${n})` : ""}
                </button>
              );
            })}
          </div>
          <span className="text-xs text-muted-foreground">
            {reports.length}
            {query.hasNextPage && total != null ? ` of ${total}` : ""}{" "}
            {reports.length === 1 && !query.hasNextPage ? "report" : "reports"}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetching}
            onClick={() => query.refetch()}
          >
            <RefreshCw className={cn("size-4", query.isFetching && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>
      <div className={cn(query.isPlaceholderData && "opacity-60 transition-opacity")}>
        <FeedbackReportsTable rows={reports} emptyText={EMPTY_TEXT[state]} />
      </div>
      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? "Loading older reports…" : "Load older reports"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
